import { create } from 'zustand';
import { supabase } from '@/lib/supabase';

export interface User {
  id: string;
  name: string;
  email: string;
  role: "admin" | "doctor" | "staff";
  status: "active" | "inactive";
  specialty?: string;
  registrationNumber?: string;
  avatarUrl?: string;
}

interface UsersState {
  users: User[];
  isLoading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  addUser: (user: Omit<User, 'id'>) => Promise<void>;
  updateUser: (user: User) => Promise<void>;
  removeUser: (id: string) => Promise<void>;
  removeUserWithCascade: (id: string) => Promise<void>;
}

export const useUsersStore = create<UsersState>()((set, get) => ({
  users: [],
  isLoading: false,
  error: null,

  fetchUsers: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('name');

      if (error) throw error;
      // Map DB (snake_case) → app model (camelCase)
      const mapped = (data || []).map((row: any) => ({
        id: row.id,
        name: row.name,
        email: row.email,
        role: row.role,
        status: row.status,
        specialty: row.specialty ?? undefined,
        registrationNumber: row.registration_number ?? undefined,
        avatarUrl: row.avatar_url ?? undefined,
      }));
      set({ users: mapped, isLoading: false });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  addUser: async (user) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('profiles')
        .insert([user]);

      if (error) throw error;
      
      // Since we're not getting the inserted data back, we'll need to refetch
      await get().fetchUsers();
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  updateUser: async (updatedUser) => {
    set({ isLoading: true, error: null });
    try {
      // Map app model (camelCase) → DB (snake_case)
      const payload: any = {
        name: updatedUser.name,
        email: updatedUser.email,
        role: updatedUser.role,
        status: updatedUser.status,
        specialty: updatedUser.specialty ?? null,
        registration_number: updatedUser.registrationNumber ?? null,
      };
      const { error } = await supabase
        .from('profiles')
        .update(payload)
        .eq('id', updatedUser.id);

      if (error) throw error;
      set((state) => ({
        users: state.users.map((user) =>
          user.id === updatedUser.id ? updatedUser : user
        ),
        isLoading: false
      }));
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  removeUser: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', id);

      if (error) throw error;
      set((state) => ({
        users: state.users.filter((user) => user.id !== id),
        isLoading: false
      }));
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  removeUserWithCascade: async (id) => {
    set({ isLoading: true, error: null });
    try {
      console.log('Starting cascade deletion for user:', id);
      
      // First, delete from doctors table if the user is a doctor
      const userToDelete = get().users.find(user => user.id === id);
      if (userToDelete?.role === 'doctor') {
        console.log('User is a doctor, deleting from doctors table');
        const { error: doctorError } = await supabase
          .from('doctors')
          .delete()
          .eq('id', id);
        
        if (doctorError) {
          console.error('Error deleting from doctors table:', doctorError);
          throw new Error(`Failed to delete doctor record: ${doctorError.message}`);
        }
        console.log('Successfully deleted from doctors table');
      }

      // Instead of deleting from profiles table, mark as inactive to disable login
      console.log('Marking user as inactive in profiles table');
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ status: 'inactive' })
        .eq('id', id);

      if (profileError) {
        console.error('Error updating profile status:', profileError);
        throw new Error(`Failed to disable user profile: ${profileError.message}`);
      }

      console.log('Successfully marked user as inactive');

      // Note: We cannot delete from Supabase Auth from client-side due to security restrictions
      // The user is effectively disabled because:
      // 1. They're marked as 'inactive' in profiles table (login will be blocked)
      // 2. They're deleted from doctors table (no doctor data)
      // 3. The login system will check for active status
      // 
      // If the user tries to login, they will be rejected due to inactive status
      console.log('User marked as inactive and deleted from doctors table - login effectively disabled');
      
      // Update local state
      set((state) => ({
        users: state.users.filter((user) => user.id !== id),
        isLoading: false
      }));
      
      console.log('Cascade deletion completed successfully');
    } catch (error) {
      console.error('Cascade deletion error:', error);
      set({ error: (error as Error).message, isLoading: false });
      throw error; // Re-throw to allow calling code to handle
    }
  },
})); 